import { useState, useEffect, useRef } from 'react'
import { Textarea } from '@/ui/components'
import { Button } from '@/ui/components'

interface WaypointNotesEditorProps {
  value: string | null | undefined
  onChange: (value: string | null) => Promise<void>
  onCancel?: () => void
  placeholder?: string
  className?: string
  isEditing?: boolean
  onEditingChange?: (isEditing: boolean) => void
  disabled?: boolean
}

/**
 * Inline notes editor component for waypoints
 * Supports save/cancel functionality
 */
export function WaypointNotesEditor({
  value,
  onChange,
  onCancel,
  placeholder = 'Add notes about this stop...',
  className = '',
  isEditing: externalIsEditing,
  onEditingChange,
  disabled = false
}: WaypointNotesEditorProps) {
  const [internalIsEditing, setInternalIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  
  // Use external editing state if provided, otherwise use internal state
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing
  const setIsEditing = (newValue: boolean) => {
    if (onEditingChange) {
      onEditingChange(newValue)
    } else {
      setInternalIsEditing(newValue)
    }
  }

  // Initialize edit value from prop
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value || '')
    }
  }, [value, isEditing])

  // Focus textarea when editing starts
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus()
      // Move cursor to end
      const length = textareaRef.current.value.length
      textareaRef.current.setSelectionRange(length, length)
    }
  }, [isEditing])

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const trimmedValue = editValue.trim()
      await onChange(trimmedValue || null)
      setIsEditing(false)
    } catch (error) {
      // Keep editing state on error
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setEditValue(value || '')
    setIsEditing(false)
    onCancel?.()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Stop propagation to prevent parent handlers from firing (e.g., card collapse on spacebar)
    e.stopPropagation()
    
    // Save on Ctrl/Cmd + Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    }
    // Cancel on Escape
    if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }

  if (!isEditing) {
    // Display mode - show notes or placeholder
    if (value) {
      return (
        <div className={`group ${className}`}>
          <div className="text-sm text-muted-foreground whitespace-pre-wrap">{value}</div>
          {!disabled && (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="mt-1 text-xs text-adventure-600 hover:text-adventure-700 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              Edit notes
            </button>
          )}
        </div>
      )
    }

    return (
      <button
        type="button"
        onClick={() => !disabled && setIsEditing(true)}
        disabled={disabled}
        className={`text-sm ${disabled ? 'text-muted-foreground cursor-default' : 'text-muted-foreground hover:text-adventure-600'} text-left ${className}`}
        title={disabled ? "Only trip owners can edit notes" : undefined}
      >
        {placeholder}
      </button>
    )
  }

  // Edit mode
  return (
    <div className={`space-y-2 ${className}`}>
      <Textarea
        ref={textareaRef}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={3}
        className="text-sm resize-none"
        disabled={disabled}
      />
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={isSaving}
          className="h-7 text-xs"
        >
          Save
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleCancel}
          disabled={isSaving}
          className="h-7 text-xs"
        >
          Cancel
        </Button>
        <span className="text-xs text-muted-foreground ml-auto">
          Ctrl+Enter to save, Esc to cancel
        </span>
      </div>
    </div>
  )
}

